import type { ApiClient } from "~/api/client";
import { filedItem, methodOf, planApplyBody } from "./commandModel";
import type {
  AppliedAction,
  ChangeRecord,
  CommandAction,
  CommandProposal,
  PlanDraft,
  ScreenContext,
  TldrResult,
} from "./commandTypes";

// The fetching half: five RPCs (AiCommandController) over the generic `api.work.call`, and the runner that performs
// what the person approved — one existing endpoint per action, in order, never stopping on a failure so the ledger
// says exactly what happened.

export async function proposeCommand(
  api: ApiClient,
  text: string,
  context: ScreenContext,
): Promise<CommandProposal> {
  const r = (await api.work.call("ai.command.propose", {
    text,
    route: context.route,
    surface: context.surface,
    itemKey: context.itemKey,
    projectKey: context.projectKey,
    documentId: context.documentId,
  })) as { proposal: CommandProposal };
  return r.proposal;
}

export async function refineText(
  api: ApiClient,
  text: string,
  instruction: string,
): Promise<string> {
  const r = (await api.work.call("ai.command.refine", { text, instruction })) as { text: string };
  return r.text;
}

export async function recordCommand(
  api: ApiClient,
  proposal: CommandProposal,
  applied: AppliedAction[],
): Promise<string | null> {
  if (applied.length === 0) return null;
  const result = (await api.work.call("ai.command.record", {
    proposalId: proposal.id,
    text: proposal.text,
    tldr: proposal.tldr,
    applied: applied.map((a) => ({ id: a.id, label: a.label, ok: a.ok, error: a.error ?? null })),
  })) as { id?: string };
  return result.id ?? null;
}

export async function listChanges(api: ApiClient, limit = 10): Promise<ChangeRecord[]> {
  const r = (await api.work.call("ai.command.changes", { limit })) as { changes: ChangeRecord[] };
  return r.changes ?? [];
}

export async function fetchTldr(api: ApiClient, context: ScreenContext): Promise<TldrResult> {
  return (await api.work.call("ai.tldr", { ...context })) as TldrResult;
}

export interface RunOutcome extends AppliedAction {
  /** A filed work item (plan actions): its key and page. */
  filed?: { key: string; url: string } | null;
}

/** Performs ONE approved action. A plan action files the EDITED draft; a call action runs its call as the server described it. */
export async function runCommandAction(
  api: ApiClient,
  action: CommandAction,
  sentence: string,
  draft?: PlanDraft | null,
): Promise<RunOutcome> {
  try {
    if (action.kind === "plan") {
      const d = draft ?? action.plan;
      if (!d?.projectKey) throw new Error("Pick a project first.");
      const result = await api.work.call("work.plan.apply", planApplyBody(d, sentence));
      return { id: action.id, label: action.label, ok: true, filed: filedItem(result) };
    }
    if (!action.call) throw new Error("Nothing to run.");
    await api.work.call(methodOf(action.call.path), action.call.body);
    for (const next of action.andThen ?? []) await api.work.call(methodOf(next.path), next.body);
    return { id: action.id, label: action.label, ok: true };
  } catch (error) {
    return {
      id: action.id,
      label: action.label,
      ok: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
}
